/**
 * Ticket Lifecycle v2 — Slice 5D-2
 *
 * Lightweight async helper that captures the *remaining-approved-work*
 * snapshot for a ticket at the moment FOH custody is accepted. The
 * resulting object is intended to be merged directly into the
 * `advisor_action_events.metadata` JSONB payload emitted by the
 * `ticket_accepted` action.
 *
 * Why this exists (slice 5D-2 rationale):
 *   The diag_plus_work bucket in `calculateAdvisorDelayImpact` needs to
 *   subtract technician work that could overlap with advisor activity.
 *   Without a snapshot at acceptance time the overlap can only be
 *   reconstructed from historical data we do not keep — so we snapshot.
 *
 * Scope: **metadata capture only.** This helper does NOT touch
 *   - `tickets.opti_time` / `tickets.opti_finish`
 *   - scheduling / Chronomancer / smart-assign / timer / parking /
 *     rollover / bay assignment
 *   - the custody axis (ticket_custody, ticket_custody_events)
 *   - Lifecycle OptiTime prediction (slice 5D-4)
 *   - advisor bucket averages (slice 5D-3)
 *
 * Boundary: imports the supabase client + the pure
 * `computeRemainingApprovedWorkMinutes` helper. It must not be imported
 * into Chronomancer / scheduling / timer / parking / rollover / bay /
 * invariants hot paths — the drift detector boundary guard pins this.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  computeRemainingApprovedWorkMinutes,
  type RemainingApprovedWorkJobLike,
  type RemainingApprovedWorkMethod,
} from './remainingApprovedWork';

/**
 * Canonical snake_case shape for the metadata fragment merged into the
 * `ticket_accepted` advisor action. Keys are snake_case because the
 * surrounding `advisor_action_events.metadata` payload uses snake_case
 * by DB convention.
 */
export interface RemainingApprovedWorkMetadata {
  remaining_approved_work_minutes: number | null;
  /**
   * Provenance tag for the captured value:
   *   - `'computed_from_ticket_jobs'`: pure helper returned a finite,
   *     non-null minutes value.
   *   - `'unavailable'`: the helper refused (insufficient_data) OR the
   *     ticket_jobs fetch failed. Caller MUST treat `null` and
   *     `'unavailable'` as a single semantic — "unknown."
   */
  remaining_approved_work_source: 'computed_from_ticket_jobs' | 'unavailable';
  /**
   * Pass-through of the pure helper's method discriminator. Useful for
   * later analytics that want to distinguish standard-minutes fallback
   * from a primary predicted-minutes computation.
   */
  remaining_approved_work_method: RemainingApprovedWorkMethod;
}

/**
 * Shape of one row returned by the ticket_jobs query below. Exported
 * so the pure mapping helper `mapTicketJobRowToJobLike` can be tested
 * in isolation without a real Supabase client.
 */
export interface TicketJobRowForCapture {
  job_status: string | null;
  predicted_minutes: number | null;
  minutes_override: number | null;
  actual_minutes: number | null;
  jobs: { standard_minutes: number | null } | null;
}

/**
 * Flatten a raw ticket_jobs row (with `jobs:job_id(standard_minutes)`
 * join) into the shape `computeRemainingApprovedWorkMinutes` expects.
 * Pure: no I/O. Exported for unit testing.
 */
export function mapTicketJobRowToJobLike(
  row: TicketJobRowForCapture,
): RemainingApprovedWorkJobLike {
  return {
    job_status: row.job_status,
    predicted_minutes: row.predicted_minutes,
    minutes_override: row.minutes_override,
    actual_minutes: row.actual_minutes,
    standard_minutes: row.jobs?.standard_minutes ?? null,
  };
}

/**
 * Pure "unavailable" sentinel reused by the failure paths below. Kept
 * as a function rather than a const so the caller always merges a
 * fresh object (no shared-mutation risk).
 */
export function unavailableRemainingApprovedWorkMetadata(): RemainingApprovedWorkMetadata {
  return {
    remaining_approved_work_minutes: null,
    remaining_approved_work_source: 'unavailable',
    remaining_approved_work_method: 'insufficient_data',
  };
}

/**
 * Fetch the snapshot for one ticket. Always resolves — failure modes
 * are folded into the `'unavailable'` sentinel rather than thrown, so
 * callers can merge the result into metadata without try/catch.
 *
 * Calling this with a fresh Supabase client per ticket is fine; the
 * helper does not cache.
 */
export async function fetchRemainingApprovedWorkForTicket(
  client: SupabaseClient,
  ticketId: string,
): Promise<RemainingApprovedWorkMetadata> {
  try {
    const { data, error } = await (client as unknown as {
      from: (table: string) => {
        select: (cols: string) => {
          eq: (col: string, val: string) => {
            is: (col: string, val: null) => Promise<{
              data: TicketJobRowForCapture[] | null;
              error: { message?: string } | null;
            }>;
          };
        };
      };
    })
      .from('ticket_jobs')
      .select(
        'job_status, predicted_minutes, minutes_override, actual_minutes, jobs:job_id(standard_minutes)',
      )
      .eq('ticket_id', ticketId)
      .is('deleted_at', null);

    if (error || !data) {
      if (error) {
        console.error(
          '[remaining-approved-work] ticket_jobs query failed',
          error,
        );
      }
      return unavailableRemainingApprovedWorkMetadata();
    }

    const jobs = data.map(mapTicketJobRowToJobLike);
    const result = computeRemainingApprovedWorkMinutes({ jobs });

    if (result.remainingApprovedWorkMinutes === null) {
      return {
        remaining_approved_work_minutes: null,
        remaining_approved_work_source: 'unavailable',
        remaining_approved_work_method: result.method,
      };
    }

    return {
      remaining_approved_work_minutes: result.remainingApprovedWorkMinutes,
      remaining_approved_work_source: 'computed_from_ticket_jobs',
      remaining_approved_work_method: result.method,
    };
  } catch (e) {
    console.error('[remaining-approved-work] unexpected error', e);
    return unavailableRemainingApprovedWorkMetadata();
  }
}
